import React from 'react';
import { EstimateResponse, parseDecimal } from '../services/BillingService';
import { personalakteStyles as ps, colors } from '../../employees/styles/personalakteStyles';

interface CostSummaryProps {
  estimate: EstimateResponse | null;
  estimateLoading: boolean;
  onApply: () => void;
  applyLabel?: string;
}

function formatCurrency(amount: number): string {
  return amount.toFixed(2).replace('.', ',') + ' €';
}

export default function CostSummary({
  estimate,
  estimateLoading,
  onApply,
  applyLabel = 'Änderungen übernehmen',
}: CostSummaryProps): React.JSX.Element {
  const hasBlockers = (estimate?.blockers?.length ?? 0) > 0;

  return (
    <div
      style={{
        ...ps.card,
        position: 'sticky',
        top: 24,
        padding: '24px 20px',
      }}
    >
      <div style={{ ...ps.sectionHeader, marginBottom: 16 }}>
        <div style={ps.sectionDot(colors.navy)} />
        Kostenübersicht
      </div>

      {estimateLoading && !estimate && (
        <div style={{ padding: 20, textAlign: 'center', color: colors.textMuted, fontSize: 13 }}>
          Berechnung...
        </div>
      )}

      {estimate && (
        <>
          <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: 12, position: 'relative' }}>
            {estimateLoading && (
              <div style={{
                position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.6)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 2, borderRadius: 8,
              }}>
                <span style={{ fontSize: 12, color: colors.textMuted }}>Aktualisierung...</span>
              </div>
            )}

            {/* Plan line */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13, color: colors.textBody }}>
              <span>{estimate.plan.name} Plan</span>
              <span>{formatCurrency(parseDecimal(estimate.plan.price_per_location))} × {estimate.plan.locations}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, fontSize: 13, fontWeight: 600, color: colors.navy }}>
              = {formatCurrency(parseDecimal(estimate.plan.subtotal))}
            </div>

            {/* Extra employees */}
            {estimate.extra_employees.packs > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, fontSize: 13, color: colors.textBody }}>
                <span>+{estimate.extra_employees.total_extra_employees} MA ({estimate.extra_employees.packs} Paket{estimate.extra_employees.packs > 1 ? 'e' : ''})</span>
                <span style={{ fontWeight: 600, color: colors.navy }}>{formatCurrency(parseDecimal(estimate.extra_employees.subtotal))}</span>
              </div>
            )}

            {/* Add-on lines */}
            {estimate.addons.map((addon) => {
                const isPerLocation = addon.billing_type === 'per_location';
                return (
                  <div key={addon.slug} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: colors.textBody }}>
                      <span>{addon.name}</span>
                      {isPerLocation ? (
                        <span>{formatCurrency(parseDecimal(addon.price_per_unit))} × {addon.quantity}</span>
                      ) : (
                        <span style={{ fontWeight: 600, color: colors.navy }}>{formatCurrency(parseDecimal(addon.subtotal))}</span>
                      )}
                    </div>
                    {isPerLocation && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 13, fontWeight: 600, color: colors.navy }}>
                        = {formatCurrency(parseDecimal(addon.subtotal))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>

          {/* Grand Total */}
          <div style={{ borderTop: `2px solid ${colors.navy}`, paddingTop: 14, marginTop: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: colors.navy }}>Gesamt / Monat</span>
              <span style={{ fontSize: 22, fontWeight: 700, color: colors.navy }}>
                {formatCurrency(parseDecimal(estimate.total_monthly))}
              </span>
            </div>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 4 }}>
              inkl. {estimate.total_employees_included} aktive Mitarbeitende
            </div>
          </div>

          {/* Blockers */}
          {estimate.blockers.map((blocker, i) => (
            <div
              key={i}
              style={{
                marginTop: 14, padding: '10px 12px', background: colors.dangerLight,
                borderRadius: 10, fontSize: 12, color: colors.danger, fontWeight: 500, lineHeight: 1.5,
              }}
            >
              {blocker}
            </div>
          ))}

          {/* Warnings */}
          {estimate.warnings.map((warning, i) => (
            <div
              key={i}
              style={{
                marginTop: 14, padding: '10px 12px', background: colors.warningLight,
                borderRadius: 10, fontSize: 12, color: colors.warning, fontWeight: 500, lineHeight: 1.5,
              }}
            >
              {warning}
            </div>
          ))}
        </>
      )}

      {/* Apply Button */}
      <button
        onClick={onApply}
        disabled={hasBlockers || estimateLoading || !estimate}
        className={hasBlockers || estimateLoading || !estimate ? undefined : 'hover-btn-primary'}
        style={{
          ...(hasBlockers || estimateLoading || !estimate ? ps.buttonDisabled : ps.buttonPrimary),
          width: '100%',
          justifyContent: 'center',
          marginTop: 16,
        }}
      >
        {applyLabel}
      </button>
    </div>
  );
}
